
import { useState, useEffect, useRef } from 'react';
import {
  fetchNorwayFireWarnings,
  fetchSwedenFireWarnings,
  evaluateFireDanger,
  type FireDangerWarning,
  type FireDangerStatus,
} from '../services/fireDangerService';
import type { Coordinates } from '../types';
import type { SupportedCountry } from '../utils/countryDetection';

const EMPTY_STATUS: FireDangerStatus = {
  isInDangerZone: false,
  isGeneralBanActive: false,
  activeWarnings: [],
  highestSeverity: null,
  dominantHazard: null,
};

export function useFireDanger(
  mapCenter: Coordinates,
  zoom: number,
  viewportCountry: SupportedCountry | null,
): { fireDanger: FireDangerStatus; isLoading: boolean } {
  const [fireDanger, setFireDanger] = useState<FireDangerStatus>(EMPTY_STATUS);
  const [isLoading, setIsLoading] = useState(false);
  const warningsRef = useRef<FireDangerWarning[]>([]);
  const fetchedCountriesRef = useRef<Set<string>>(new Set());
  const fetchingRef = useRef(false);

  useEffect(() => {
    if (zoom < 7 || !viewportCountry) {
      setFireDanger(EMPTY_STATUS);
      return;
    }

    const needsNorway = viewportCountry === 'NO' && !fetchedCountriesRef.current.has('NO');
    const needsSweden = viewportCountry === 'SE' && !fetchedCountriesRef.current.has('SE');

    if (!needsNorway && !needsSweden) {
      // Re-evaluate position against cached warnings
      const status = evaluateFireDanger(
        mapCenter.latitude, mapCenter.longitude,
        viewportCountry, warningsRef.current,
      );
      setFireDanger(status);
      return;
    }

    if (fetchingRef.current) return;

    const timer = setTimeout(async () => {
      fetchingRef.current = true;
      setIsLoading(true);
      try {
        const promises: Promise<FireDangerWarning[]>[] = [];
        if (needsNorway) promises.push(fetchNorwayFireWarnings());
        if (needsSweden) promises.push(fetchSwedenFireWarnings());

        const results = await Promise.all(promises);
        const newWarnings = results.flat();
        warningsRef.current = [...warningsRef.current, ...newWarnings];

        if (needsNorway) fetchedCountriesRef.current.add('NO');
        if (needsSweden) fetchedCountriesRef.current.add('SE');

        const status = evaluateFireDanger(
          mapCenter.latitude, mapCenter.longitude,
          viewportCountry, warningsRef.current,
        );
        setFireDanger(status);
      } catch (e) {
        console.error('Fire danger fetch failed:', e);
      } finally {
        fetchingRef.current = false;
        setIsLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [mapCenter.latitude, mapCenter.longitude, zoom, viewportCountry]);

  return { fireDanger, isLoading };
}
